import WallChrome from "./WallChrome";
import React, { useState } from "react";
import ProofAvatar from "./../../widget/public/shared/ProofAvatar";
import ProofText from "./../../widget/public/shared/ProofText";
import Stars from "./../../widget/public/shared/Stars";
import MediaThumbnail from "./../../widget/public/shared/MediaThumbnail";
import AudioPlayer from "./../../widget/public/shared/AudioPlayer";
import SourceBadge from "./../../widget/public/shared/SourceBadge";
import LoadMoreSentinel from "./../../widget/public/shared/LoadMoreSentinel";
import Lightbox from "./../../widget/public/shared/Lightbox";
import HeartButton from "./../../widget/public/shared/HeartButton";
import useContainerColumns from "./../../widget/public/shared/useContainerColumns";
import useAutoScroll from "./../../widget/public/shared/useAutoScroll";
import { readableText } from "./../../widget/public/shared/colorUtils";

// Tailwind-aligned breakpoints (sm/lg/xl): everything phone-sized stays
// at 1 column, including phones in landscape (~800–930px). Tablets and
// small windows get 2 columns; full desktop gets 3–4.
const COLUMN_BREAKPOINTS = [
  { min: 1280, cols: 4 },
  { min: 1024, cols: 3 },
  { min: 640,  cols: 2 },
  { min: 0,    cols: 1 },
];

const distribute = (proofs, cols) => {
  const buckets = Array.from({ length: cols }, () => []);
  (proofs || []).forEach((p, i) => buckets[i % cols].push(p));
  return buckets;
};

export const Masonry = ({ wall, proofs: wallProofs, loaded, loadMore, loadingMore, exhausted }) => {
  const widget = wall;
  const initProofs = wallProofs;
  const [cols, setColsRef] = useContainerColumns(COLUMN_BREAKPOINTS, 1);
  const autoScroll = !!widget.design?.auto_scroll;
  const scrollSpeed = Number(widget.design?.scroll_speed) || 30;
  const setScrollRef = useAutoScroll({
    enabled: autoScroll && !loadMore, // infinite-scroll widgets shouldn't auto-loop
    direction: "down",
    speed: scrollSpeed,
  });
  // Container needs both refs (column observer + scroller). Compose them.
  const setContainerRef = (node) => { setColsRef(node); setScrollRef(node); };

  const lightboxEnabled = widget.design?.lightbox !== false;
  const likesEnabled = widget.design?.likes === true;
  const [active, setActive] = useState(null);

  const buckets = distribute(initProofs, cols);
  const bg = widget.bg_color || "#f8fafc";
  const accent = widget.primary_color || "#3E8DFF";
  const fg = (widget.text_color || readableText(bg));

  return (
    <WallChrome wall={wall} loaded={loaded} count={(initProofs || []).length}>
    <div ref={setContainerRef} className="w-full">
      <div className="grid gap-5 sm:gap-6" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {buckets.map((bucket, ci) => (
          <div key={`col-${ci}`} className="flex flex-col gap-4 sm:gap-5">
            {bucket.map((proof) => (
              <Card
                key={proof.uuid}
                proof={proof}
                accent={accent}
                fg={fg}
                textColor={widget.text_color}
                onClick={lightboxEnabled ? () => setActive(proof) : undefined}
                showLike={likesEnabled}
              />
            ))}
          </div>
        ))}
      </div>
      {loadMore && (
        <LoadMoreSentinel
          onVisible={loadMore}
          loading={loadingMore}
          exhausted={exhausted}
          color={accent}
        />
      )}
      <Lightbox
        proof={active}
        accent={accent}
        open={!!active}
        onClose={() => setActive(null)}
      />
    </div>
    </WallChrome>
  );
};

const Card = ({ proof, accent, onClick, showLike, textColor }) => {
  const isVideo = proof.kind === "video" && proof.video_url;
  const isAudio = proof.kind === "audio" && proof.audio_url;
  // Video cards keep their own click handler (the play button), and audio
  // cards have their own player controls — so disable card-level click for
  // those to avoid hijacking the in-card interactions.
  const cardClickable = !!onClick && !isVideo && !isAudio;
  const Wrapper = cardClickable ? "button" : "div";

  return (
    <Wrapper
      type={cardClickable ? "button" : undefined}
      onClick={cardClickable ? onClick : undefined}
      className={`bg-white border-[2px] border-black overflow-hidden shadow-[3px_3px_0_rgba(0,0,0,0.18)] hover:shadow-[5px_5px_0_rgba(0,0,0,0.28)] hover:-translate-y-0.5 transition-all w-full text-left ${cardClickable ? "cursor-pointer" : ""}`}>
      {isVideo && (
        <div className="aspect-video w-full bg-black">
          <MediaThumbnail proof={proof} className="w-full h-full block" />
        </div>
      )}
      <div className="p-4 flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5">
          <ProofAvatar proof={proof} size={36} ringColor={accent} />
          <div className="flex-1 min-w-0">
            <div className="font-black text-sm text-slate-900 truncate">{proof.name}</div>
            {proof.contact_title && (
              <div className="text-[11px] font-bold text-slate-500 truncate">
                {proof.contact_title}
                {proof.contact_company_name ? ` · ${proof.contact_company_name}` : ""}
              </div>
            )}
          </div>
          <SourceBadge proof={proof} color={accent} size={14} />
        </div>

        {!isVideo && proof.rating != null && (
          <Stars rating={proof.rating} color="#FFD23A" />
        )}

        {isAudio && (
          <div className="py-1">
            <AudioPlayer src={proof.audio_url} primaryColor={accent} textColor={textColor || "#0f172a"} barColor="#cbd5e1" />
          </div>
        )}

        {proof.headline && !isVideo && (
          <div className="font-black text-base text-slate-900 leading-tight">{proof.headline}</div>
        )}
        {!isAudio && (
          <ProofText
            proof={proof}
            className="text-sm text-slate-700 leading-relaxed"
          />
        )}
        {showLike && (
          <div className="flex justify-end pt-1 text-slate-400">
            <HeartButton proof={proof} accent={accent} />
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default Masonry;
